import { apiRequest } from '@/lib/queryClient';

// Printify API configuration
const PRINTIFY_API_BASE = 'https://api.printify.com/v1';

// Types for Printify API responses
export interface PrintifyShop {
  id: string;
  title: string;
  sales_channel: string;
  country: string;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface PrintifyProduct {
  id: string;
  title: string;
  description: string;
  tags: string[];
  is_enabled: boolean;
  is_printify_express: boolean;
  sales_channel_properties: any[];
  created_at: string;
  updated_at: string;
  published_at: string;
  blueprint_id: number;
  print_provider_id: number;
  variants: PrintifyProductVariant[];
  images: PrintifyProductImage[];
  print_areas: any[];
  print_details: any[];
}

export interface PrintifyProductVariant {
  id: number;
  title: string;
  options: any[];
  placeholders: any[];
  is_enabled: boolean;
  is_default: boolean;
  price: number;
  weight: number;
  weight_unit: string;
  sku: string;
  barcode: string;
  cost: number;
  currency: string;
  is_printify_express: boolean;
  is_printify_express_eligible: boolean;
  print_provider_id: number;
  print_areas: any[];
  print_details: any[];
}

export interface PrintifyProductImage {
  id: number;
  src: string;
  variant_ids: number[];
  position: string;
  is_preview: boolean;
  created_at: string;
  updated_at: string;
}

export interface PrintifyOrder {
  id: string;
  label: string;
  order_number: number;
  created_at: string;
  updated_at: string;
  status: string;
  total_price: number;
  currency: string;
  shipping_method: number;
  send_shipping_notification: boolean;
  line_items: PrintifyOrderLineItem[];
  shipping_address: PrintifyShippingAddress;
  send_fulfillment_notification: boolean;
  tags: string[];
  note: string;
}

export interface PrintifyOrderLineItem {
  id: number;
  product_id: string;
  variant_id: number;
  quantity: number;
  price: number;
  currency: string;
  cost: number;
  sku: string;
  title: string;
  options: any[];
  print_provider_id: number;
  print_areas: any[];
  print_details: any[];
}

export interface PrintifyShippingAddress {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country: string;
  region: string;
  city: string;
  address1: string;
  address2: string;
  zip: string;
}

// Helper function to make authenticated requests to Printify API via our backend
async function printifyRequest(endpoint: string, options: RequestInit = {}) {
  // Use our backend API to make Printify requests (which handles the API token securely)
  const method = options.method || 'GET';
  const data = options.body ? JSON.parse(options.body as string) : undefined;
  
  // apiRequest returns parsed JSON and throws on non-2xx responses
  const response = await apiRequest(method, `/api/admin/printify${endpoint}`, data) as any;
  return response.data || response; // Handle both { data: ... } and direct response formats
}

// Printify API service
export const printifyService = {
  // Get all shops
  async getShops(): Promise<PrintifyShop[]> {
    return printifyRequest('/shops.json');
  },

  // Get products from a specific shop
  async getShopProducts(shopId: string, page: number = 1, limit: number = 50): Promise<{
    current_page: number;
    data: PrintifyProduct[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  }> {
    return printifyRequest(`/shops/${shopId}/products.json?page=${page}&limit=${limit}`);
  },

  // Get a specific product
  async getProduct(shopId: string, productId: string): Promise<PrintifyProduct> {
    return printifyRequest(`/shops/${shopId}/products/${productId}.json`);
  },

  // Create an order
  async createOrder(shopId: string, orderData: {
    label: string;
    line_items: Array<{
      product_id: string;
      variant_id: number;
      quantity: number;
      print_provider_id: number;
      print_areas: any[];
    }>;
    shipping_address: PrintifyShippingAddress;
    send_fulfillment_notification: boolean;
    send_shipping_notification: boolean;
    note?: string;
  }): Promise<PrintifyOrder> {
    return printifyRequest(`/shops/${shopId}/orders.json`, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  // Get order status
  async getOrder(shopId: string, orderId: string): Promise<PrintifyOrder> {
    return printifyRequest(`/shops/${shopId}/orders/${orderId}.json`);
  },

  // Get all orders from a shop
  async getShopOrders(shopId: string, page: number = 1, limit: number = 50): Promise<{
    current_page: number;
    data: PrintifyOrder[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  }> {
    return printifyRequest(`/shops/${shopId}/orders.json?page=${page}&limit=${limit}`);
  },

  // Get print providers for a specific blueprint
  async getPrintProviders(blueprintId: number): Promise<any[]> {
    return printifyRequest(`/catalog/blueprints/${blueprintId}/print_providers.json`);
  },

  // Get print provider variants
  async getPrintProviderVariants(blueprintId: number, printProviderId: number): Promise<any[]> {
    return printifyRequest(`/catalog/blueprints/${blueprintId}/print_providers/${printProviderId}/variants.json`);
  },

  // Get available blueprints (product types)
  async getBlueprints(): Promise<any[]> {
    return printifyRequest('/catalog/blueprints.json');
  },

  // Get blueprint details
  async getBlueprint(blueprintId: number): Promise<any> {
    return printifyRequest(`/catalog/blueprints/${blueprintId}.json`);
  },
};

// Utility functions for transforming Printify data to our app's format
export const printifyUtils = {
  // Transform Printify product to our merchandise format
  transformProduct(printifyProduct: PrintifyProduct, shopId: string) {
    const defaultVariant = printifyProduct.variants.find(v => v.is_default) || printifyProduct.variants[0];
    const mainImage = printifyProduct.images.find(img => img.is_preview) || printifyProduct.images[0];
    
    return {
      id: printifyProduct.id,
      name: printifyProduct.title,
      slug: printifyProduct.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      productType: 'printify_product',
      category: this.determineCategory(printifyProduct.tags),
      description: printifyProduct.description || 'High-quality custom product from Printify',
      price: (defaultVariant?.price || 0) / 100, // Convert cents to dollars
      images: printifyProduct.images.map(img => img.src),
      features: printifyProduct.tags,
      sizes: this.extractSizes(printifyProduct.variants),
      colors: this.extractColors(printifyProduct.variants),
      inStock: printifyProduct.is_enabled,
      featured: false,
      sortOrder: 0,
      metadata: {
        printifyProductId: printifyProduct.id,
        shopId,
        blueprintId: printifyProduct.blueprint_id,
        printProviderId: printifyProduct.print_provider_id,
        variants: printifyProduct.variants,
        printAreas: printifyProduct.print_areas,
        printDetails: printifyProduct.print_details,
      },
    };
  },

  // Determine product category based on tags
  determineCategory(tags: string[]): string {
    const tagString = tags.join(' ').toLowerCase();
    
    if (tagString.includes('shirt') || tagString.includes('hoodie') || tagString.includes('sweatshirt')) {
      return 'apparel';
    } else if (tagString.includes('mug') || tagString.includes('cup')) {
      return 'accessories';
    } else if (tagString.includes('sticker') || tagString.includes('poster')) {
      return 'stationery';
    } else {
      return 'accessories';
    }
  },

  // Extract available sizes from variants
  extractSizes(variants: PrintifyProductVariant[]): string[] {
    const sizes = new Set<string>();
    variants.forEach(variant => {
      if (variant.title.toLowerCase().includes('size')) {
        const sizeMatch = variant.title.match(/(\w+)/);
        if (sizeMatch) sizes.add(sizeMatch[1]);
      }
    });
    return Array.from(sizes);
  },

  // Extract available colors from variants
  extractColors(variants: PrintifyProductVariant[]): string[] {
    const colors = new Set<string>();
    variants.forEach(variant => {
      if (variant.title.toLowerCase().includes('color')) {
        const colorMatch = variant.title.match(/(\w+)/);
        if (colorMatch) colors.add(colorMatch[1]);
      }
    });
    return Array.from(colors);
  },

  // Format price for display
  formatPrice(priceInCents: number): string {
    return `$${(priceInCents / 100).toFixed(2)}`;
  },

  // Get the best image for a product
  getBestImage(images: PrintifyProductImage[]): string {
    const previewImage = images.find(img => img.is_preview);
    if (previewImage) return previewImage.src;
    
    const firstImage = images[0];
    return firstImage ? firstImage.src : '/placeholder-product.jpg';
  },
};
